/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { speciesNames } from "../utils/speciesNames";
import {
  classesByPhylum,
  familiesByOrder,
  generaByFamily,
  ordersByClass,
  phylaByKingdom,
} from "../utils/taxonomy";

const RELATIONSHIP_DEFINITIONS = {
  pollinates: {
    sourceLabel: "Pollinates",
    targetLabel: "Pollinated by",
    icon: "pollination",
  },
  visitsFlowersOf: {
    sourceLabel: "Visits flowers of",
    targetLabel: "Flowers visited by",
    icon: "flower",
  },
  dispersesSeedsOf: {
    sourceLabel: "Disperses seeds of",
    targetLabel: "Dispersed by",
    icon: "seed",
  },
  consumesFruitsOf: {
    sourceLabel: "Consumes fruits of",
    targetLabel: "Fruits consumed by",
    icon: "feeding",
  },
  consumesSeedsOf: {
    sourceLabel: "Consumes seeds of",
    targetLabel: "Seeds consumed by",
    icon: "seed",
  },
  preysOn: {
    sourceLabel: "Preys on",
    targetLabel: "Predated by",
    icon: "predation",
  },
  feedsOn: {
    sourceLabel: "Feeds on",
    targetLabel: "Eaten by",
    plantTargetLabel: "Herbivorized by",
    icon: "feeding",
  },
  parasitizes: {
    sourceLabel: "Parasitizes",
    targetLabel: "Parasitized by",
    icon: "parasite",
  },
  infects: {
    sourceLabel: "Infects",
    targetLabel: "Infected by",
    icon: "infection",
  },
  decomposes: {
    sourceLabel: "Decomposes",
    targetLabel: "Decomposed by",
    icon: "decomposition",
  },
  mycorrhizalWith: {
    sourceLabel: "Mycorrhizal association with",
    targetLabel: "Mycorrhizal association with",
    symmetric: true,
    icon: "mycorrhiza",
  },
  mutualisticWith: {
    sourceLabel: "Mutualistic relationship with",
    targetLabel: "Mutualistic relationship with",
    symmetric: true,
    icon: "mutualism",
  },
  competesWith: {
    sourceLabel: "Competes with",
    targetLabel: "Competes with",
    symmetric: true,
    icon: "competition",
  },
};

const ABIOTIC_AGENTS = [
  {
    agentType: "wind",
    scientific: "Wind",
    common: "Abiotic agent",
    description: "Pollen or diaspores carried by moving air.",
  },
  {
    agentType: "water",
    scientific: "Water",
    common: "Abiotic agent",
    description: "Pollen or diaspores carried by water.",
  },
  {
    agentType: "gravity",
    scientific: "Gravity",
    common: "Abiotic dispersal agent",
    description: "Fruits or seeds fall and move under gravity.",
  },
  {
    agentType: "ballistic",
    scientific: "Ballistic release",
    common: "Abiotic dispersal mechanism",
    description: "The plant explosively ejects its seeds or fruits.",
  },
  {
    agentType: "rain-splash",
    scientific: "Rain splash",
    common: "Abiotic dispersal agent",
    description: "Raindrop impacts move seeds or other diaspores.",
  },
];

const ABIOTIC_AGENT_TYPES_BY_RELATIONSHIP = {
  pollinates: new Set(["wind", "water"]),
  dispersesSeedsOf: new Set([
    "wind",
    "water",
    "gravity",
    "ballistic",
    "rain-splash",
  ]),
};

const COUNT_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
];

const SHARED_CHOICES = [
  {
    id: "mutualistic-with",
    label: "Mutualistic relationship with",
    description: "Both species benefit from the interaction.",
    predicate: "mutualisticWith",
    direction: "symmetric",
  },
  {
    id: "competes-with",
    label: "Competes with",
    description: "The species compete for space, food, or another resource.",
    predicate: "competesWith",
    direction: "symmetric",
  },
];

const CHOICES_BY_KINGDOM = {
  plantae: [
    {
      id: "pollinated-by",
      label: "Pollinated by",
      description: "The selected species transfers pollen for this plant.",
      predicate: "pollinates",
      direction: "incoming",
    },
    {
      id: "flowers-visited-by",
      label: "Flowers visited by",
      description: "A floral visitor without assuming confirmed pollination.",
      predicate: "visitsFlowersOf",
      direction: "incoming",
    },
    {
      id: "dispersed-by",
      label: "Dispersed by",
      description:
        "A species or abiotic agent moves viable seeds or fruits away from the parent plant.",
      predicate: "dispersesSeedsOf",
      direction: "incoming",
    },
    {
      id: "fruits-consumed-by",
      label: "Fruits consumed by",
      description:
        "The selected species eats this plant's fruit without assuming effective seed dispersal.",
      predicate: "consumesFruitsOf",
      direction: "incoming",
    },
    {
      id: "seeds-consumed-by",
      label: "Seeds consumed by",
      description:
        "The selected species consumes or destroys this plant's seeds without dispersing them.",
      predicate: "consumesSeedsOf",
      direction: "incoming",
    },
    {
      id: "herbivorized-by",
      label: "Herbivorized by",
      description: "The selected species feeds on this plant.",
      predicate: "feedsOn",
      direction: "incoming",
    },
    {
      id: "parasitized-by",
      label: "Parasitized by",
      description: "The selected species lives at this plant's expense.",
      predicate: "parasitizes",
      direction: "incoming",
    },
    {
      id: "infected-by",
      label: "Infected by",
      description: "The selected species causes an infection or disease.",
      predicate: "infects",
      direction: "incoming",
    },
    {
      id: "mycorrhizal-with",
      label: "Mycorrhizal association with",
      description: "A documented mycorrhizal association between both species.",
      predicate: "mycorrhizalWith",
      direction: "symmetric",
    },
    ...SHARED_CHOICES,
  ],
  animalia: [
    {
      id: "preys-on",
      label: "Preys on",
      description: "This species kills and consumes the selected species.",
      predicate: "preysOn",
      direction: "outgoing",
    },
    {
      id: "predated-by",
      label: "Predated by",
      description: "The selected species kills and consumes this species.",
      predicate: "preysOn",
      direction: "incoming",
    },
    {
      id: "feeds-on",
      label: "Feeds on",
      description: "This species consumes material from the selected species.",
      predicate: "feedsOn",
      direction: "outgoing",
    },
    {
      id: "pollinates",
      label: "Pollinates",
      description: "This species transfers pollen for the selected plant.",
      predicate: "pollinates",
      direction: "outgoing",
    },
    {
      id: "visits-flowers-of",
      label: "Visits flowers of",
      description: "This species visits the selected plant's flowers.",
      predicate: "visitsFlowersOf",
      direction: "outgoing",
    },
    {
      id: "disperses-seeds-of",
      label: "Disperses seeds of",
      description:
        "This species moves viable seeds or fruits away from the parent plant.",
      predicate: "dispersesSeedsOf",
      direction: "outgoing",
    },
    {
      id: "consumes-fruits-of",
      label: "Consumes fruits of",
      description:
        "This species eats the selected plant's fruit without assuming effective seed dispersal.",
      predicate: "consumesFruitsOf",
      direction: "outgoing",
    },
    {
      id: "consumes-seeds-of",
      label: "Consumes seeds of",
      description:
        "This species consumes or destroys the selected plant's seeds without dispersing them.",
      predicate: "consumesSeedsOf",
      direction: "outgoing",
    },
    {
      id: "parasitizes",
      label: "Parasitizes",
      description: "This species lives at the selected species' expense.",
      predicate: "parasitizes",
      direction: "outgoing",
    },
    {
      id: "parasitized-by",
      label: "Parasitized by",
      description: "The selected species lives at this species' expense.",
      predicate: "parasitizes",
      direction: "incoming",
    },
    {
      id: "infected-by",
      label: "Infected by",
      description: "The selected species causes an infection or disease.",
      predicate: "infects",
      direction: "incoming",
    },
    ...SHARED_CHOICES,
  ],
  fungi: [
    {
      id: "preys-on",
      label: "Preys on",
      description: "This fungus captures and consumes the selected species.",
      predicate: "preysOn",
      direction: "outgoing",
    },
    {
      id: "predated-by",
      label: "Predated by",
      description: "The selected species captures and consumes this fungus.",
      predicate: "preysOn",
      direction: "incoming",
    },
    {
      id: "parasitizes",
      label: "Parasitizes",
      description: "This fungus lives at the selected species' expense.",
      predicate: "parasitizes",
      direction: "outgoing",
    },
    {
      id: "parasitized-by",
      label: "Parasitized by",
      description: "The selected species parasitizes this fungus.",
      predicate: "parasitizes",
      direction: "incoming",
    },
    {
      id: "infects",
      label: "Infects",
      description: "This fungus infects the selected species.",
      predicate: "infects",
      direction: "outgoing",
    },
    {
      id: "infected-by",
      label: "Infected by",
      description: "The selected species infects this fungus.",
      predicate: "infects",
      direction: "incoming",
    },
    {
      id: "decomposes",
      label: "Decomposes",
      description: "This fungus decomposes material from the selected species.",
      predicate: "decomposes",
      direction: "outgoing",
    },
    {
      id: "decomposed-by",
      label: "Decomposed by",
      description: "The selected species decomposes material from this fungus.",
      predicate: "decomposes",
      direction: "incoming",
    },
    {
      id: "mycorrhizal-with",
      label: "Mycorrhizal association with",
      description: "A documented mycorrhizal association between both species.",
      predicate: "mycorrhizalWith",
      direction: "symmetric",
    },
    {
      id: "eaten-by",
      label: "Eaten by",
      description: "The selected species feeds on this fungus.",
      predicate: "feedsOn",
      direction: "incoming",
    },
    ...SHARED_CHOICES,
  ],
};

const FALLBACK_CHOICES = [
  {
    id: "preys-on",
    label: "Preys on",
    description: "This species kills and consumes the selected species.",
    predicate: "preysOn",
    direction: "outgoing",
  },
  {
    id: "predated-by",
    label: "Predated by",
    description: "The selected species kills and consumes this species.",
    predicate: "preysOn",
    direction: "incoming",
  },
  {
    id: "feeds-on",
    label: "Feeds on",
    description: "This species consumes material from the selected species.",
    predicate: "feedsOn",
    direction: "outgoing",
  },
  {
    id: "parasitizes",
    label: "Parasitizes",
    description: "This species lives at the selected species' expense.",
    predicate: "parasitizes",
    direction: "outgoing",
  },
  {
    id: "parasitized-by",
    label: "Parasitized by",
    description: "The selected species lives at this species' expense.",
    predicate: "parasitizes",
    direction: "incoming",
  },
  ...SHARED_CHOICES,
];

function normalizeSpeciesName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function formatRelationshipCount(value) {
  return COUNT_WORDS[value] || String(value);
}

function getGroupedRelationshipSummary(group) {
  const count = group.relationships.length;
  if (count < 2) return "";

  const abioticCount = group.relationships.filter(
    (relationship) => relationship.relatedEntityType === "abiotic",
  ).length;
  const subject =
    abioticCount === 0
      ? "species"
      : abioticCount === count
        ? "abiotic agents"
        : "agents";

  return `${group.label} ${formatRelationshipCount(count)} ${subject}`;
}

function routeSegment(value) {
  return encodeURIComponent(normalizeSpeciesName(value));
}

function findParent(groups, childName) {
  const childKey = normalizeSpeciesName(childName);
  if (!childKey) return "";

  for (const [parentName, children] of Object.entries(groups || {})) {
    if (
      (children || []).some((child) => normalizeSpeciesName(child) === childKey)
    ) {
      return String(parentName || "").trim();
    }
  }

  return "";
}

function resolveTaxonomyPath(scientificName) {
  const genus = String(scientificName || "")
    .trim()
    .split(/\s+/)[0];
  const family = findParent(generaByFamily, genus);
  const order = findParent(familiesByOrder, family);
  const className = findParent(ordersByClass, order);
  const phylum = findParent(classesByPhylum, className);
  const kingdom = findParent(phylaByKingdom, phylum);

  if (!genus || !family || !order || !className || !phylum || !kingdom) {
    return null;
  }

  return { genus, family, order, className, phylum, kingdom };
}

function getKingdom(scientificName, suppliedKingdom) {
  return (
    String(suppliedKingdom || "").trim() ||
    resolveTaxonomyPath(scientificName)?.kingdom ||
    ""
  );
}

function getRelationshipView(relationship, currentSpeciesKey) {
  const definition = RELATIONSHIP_DEFINITIONS[relationship.relationshipType];
  if (!definition) return null;

  const currentIsSource = relationship.sourceSpeciesKey === currentSpeciesKey;
  const relatedScientificName = currentIsSource
    ? relationship.targetScientificName
    : relationship.sourceScientificName;
  const relatedCommonName = currentIsSource
    ? relationship.targetCommonName
    : relationship.sourceCommonName;
  const relatedSpeciesKey = currentIsSource
    ? relationship.targetSpeciesKey
    : relationship.sourceSpeciesKey;
  const relatedEntityType = currentIsSource
    ? relationship.targetEntityType || "species"
    : relationship.sourceEntityType || "species";
  const relatedAgentType = currentIsSource
    ? relationship.targetAgentType || ""
    : relationship.sourceAgentType || "";
  const currentKingdom = currentIsSource
    ? relationship.sourceKingdom
    : relationship.targetKingdom;

  let label = currentIsSource ? definition.sourceLabel : definition.targetLabel;

  if (
    relationship.relationshipType === "feedsOn" &&
    !currentIsSource &&
    normalizeSpeciesName(currentKingdom) === "plantae"
  ) {
    label = definition.plantTargetLabel;
  }

  return {
    ...relationship,
    label,
    icon: definition.icon,
    relatedScientificName,
    relatedCommonName,
    relatedSpeciesKey,
    relatedEntityType,
    relatedAgentType,
  };
}

function relationshipEndpoints(choice, currentSpecies, selectedSpecies) {
  if (choice.direction === "incoming") {
    return { source: selectedSpecies, target: currentSpecies };
  }

  if (choice.direction === "symmetric") {
    return [currentSpecies, selectedSpecies]
      .sort((first, second) =>
        first.scientificNameKey.localeCompare(second.scientificNameKey),
      )
      .reduce(
        (result, species, index) => ({
          ...result,
          [index === 0 ? "source" : "target"]: species,
        }),
        {},
      );
  }

  return { source: currentSpecies, target: selectedSpecies };
}

function RelationshipIcon({ kind = "mutualism", className = "h-5 w-5" }) {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    className,
  };

  if (kind === "pollination" || kind === "flower") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="2.2" />
        <path d="M12 9.8C10.2 6.2 11 3.5 12 3c1 .5 1.8 3.2 0 6.8ZM14.2 12c3.6-1.8 6.3-1 6.8 0-.5 1-3.2 1.8-6.8 0ZM12 14.2c1.8 3.6 1 6.3 0 6.8-1-.5-1.8-3.2 0-6.8ZM9.8 12c-3.6 1.8-6.3 1-6.8 0 .5-1 3.2-1.8 6.8 0Z" />
      </svg>
    );
  }

  if (kind === "seed") {
    return (
      <svg {...commonProps}>
        <path d="M5 18c6.5.3 11-3.4 14-12-8.6.3-13.2 4.1-14 12Z" />
        <path d="M5 18c3.2-3.5 6.5-5.8 10.5-7.7M6.8 15.9l-.4-4M10.2 12.8l3.8.4" />
      </svg>
    );
  }

  if (kind === "predation" || kind === "feeding") {
    return (
      <svg {...commonProps}>
        <path d="M4 7.5c3.7-3 8-3.1 12-.4l4-2-1.2 4.1 1.2 4.1-4-2c-4 2.7-8.3 2.6-12-.4l2-1.7-2-1.7Z" />
        <circle cx="14.7" cy="8" r=".7" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (kind === "parasite" || kind === "infection") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 5V3M12 21v-2M5 12H3M21 12h-2M7.1 7.1 5.7 5.7M18.3 18.3l-1.4-1.4M16.9 7.1l1.4-1.4M5.7 18.3l1.4-1.4" />
        <circle cx="10.5" cy="10.8" r=".6" fill="currentColor" stroke="none" />
        <circle cx="13.5" cy="13.2" r=".6" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (kind === "decomposition") {
    return (
      <svg {...commonProps}>
        <path d="M6 17c1-5.5 4.5-9 11-10-1 6.5-4.6 10-11 10Z" />
        <path d="M6 17c3-2.7 6-4.7 9.3-6.3M5 20h14" />
      </svg>
    );
  }

  if (kind === "mycorrhiza") {
    return (
      <svg {...commonProps}>
        <path d="M12 3v9M12 7 8 4M12 9l4-3M12 12c-3 1-4.5 3.7-4.5 7M12 12c3 1 4.5 3.7 4.5 7M9.2 14.2 5 12M14.8 14.2 19 12M7.8 17l-3 1.5M16.2 17l3 1.5" />
      </svg>
    );
  }

  if (kind === "competition") {
    return (
      <svg {...commonProps}>
        <path d="M8 5 4 9l4 4M4 9h11M16 19l4-4-4-4M20 15H9" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <circle cx="7" cy="12" r="3" />
      <circle cx="17" cy="12" r="3" />
      <path d="M10 12h4M7 9V6M17 15v3" />
    </svg>
  );
}

export default function EcologicalRelationships({
  scientificName,
  commonName = "",
  kingdom = "",
  collectionOwnerUid,
  enabled = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const functions = useMemo(
    () => getFunctions(undefined, "southamerica-east1"),
    [],
  );
  const modalRef = useRef(null);
  const currentSpeciesKey = normalizeSpeciesName(scientificName);
  const resolvedKingdom = getKingdom(scientificName, kingdom);
  const kingdomKey = normalizeSpeciesName(resolvedKingdom);
  const ownerUid = collectionOwnerUid || auth.currentUser?.uid || "";

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("browse");
  const [relationships, setRelationships] = useState([]);
  const [relationshipError, setRelationshipError] = useState("");
  const [reportedRelationshipIds, setReportedRelationshipIds] = useState(
    new Set(),
  );
  const [
    retractionStatusByRelationshipId,
    setRetractionStatusByRelationshipId,
  ] = useState(new Map());
  const [collectionSpecies, setCollectionSpecies] = useState(new Map());
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [speciesQuery, setSpeciesQuery] = useState("");
  const [selectedRelatedSpecies, setSelectedRelatedSpecies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [requestingRetractionId, setRequestingRetractionId] = useState("");
  const [linkedSpeciesTransition, setLinkedSpeciesTransition] = useState(null);
  const [retractionTarget, setRetractionTarget] = useState(null);
  const [retractionReason, setRetractionReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const choices = useMemo(
    () => CHOICES_BY_KINGDOM[kingdomKey] || FALLBACK_CHOICES,
    [kingdomKey],
  );
  const availableAbioticAgents = useMemo(() => {
    if (!selectedChoice || selectedChoice.direction !== "incoming") return [];

    const allowedAgentTypes =
      ABIOTIC_AGENT_TYPES_BY_RELATIONSHIP[selectedChoice.predicate];
    if (!allowedAgentTypes) return [];

    return ABIOTIC_AGENTS.filter((agent) =>
      allowedAgentTypes.has(agent.agentType),
    );
  }, [selectedChoice]);

  useEffect(() => {
    if (!enabled || !currentSpeciesKey || !auth.currentUser) {
      setRelationships([]);
      return undefined;
    }

    const relationshipCollection = collection(
      db,
      "speciesEcologicalRelationships",
    );
    let sourceResults = [];
    let targetResults = [];

    function publishResults() {
      const merged = new Map();
      [...sourceResults, ...targetResults]
        .filter((item) => item.status !== "retracted")
        .forEach((item) => merged.set(item.id, item));
      setRelationships([...merged.values()]);
    }

    const unsubscribeSource = onSnapshot(
      query(
        relationshipCollection,
        where("sourceSpeciesKey", "==", currentSpeciesKey),
      ),
      (snapshot) => {
        sourceResults = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setRelationshipError("");
        publishResults();
      },
      (error) => {
        console.error(
          "Failed to load outgoing ecological relationships:",
          error,
        );
        setRelationshipError("Ecological relationships could not be loaded.");
      },
    );

    const unsubscribeTarget = onSnapshot(
      query(
        relationshipCollection,
        where("targetSpeciesKey", "==", currentSpeciesKey),
      ),
      (snapshot) => {
        targetResults = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setRelationshipError("");
        publishResults();
      },
      (error) => {
        console.error(
          "Failed to load incoming ecological relationships:",
          error,
        );
        setRelationshipError("Ecological relationships could not be loaded.");
      },
    );

    return () => {
      unsubscribeSource();
      unsubscribeTarget();
    };
  }, [currentSpeciesKey, enabled]);

  useEffect(() => {
    if (!auth.currentUser || relationships.length === 0) {
      setReportedRelationshipIds(new Set());
      setRetractionStatusByRelationshipId(new Map());
      return undefined;
    }

    const unsubscribers = [];
    setReportedRelationshipIds(new Set());
    setRetractionStatusByRelationshipId(new Map());

    relationships.forEach((relationship) => {
      unsubscribers.push(
        onSnapshot(
          doc(
            db,
            "speciesEcologicalRelationships",
            relationship.id,
            "reports",
            auth.currentUser.uid,
          ),
          (snapshot) => {
            setReportedRelationshipIds((previous) => {
              const next = new Set(previous);
              if (snapshot.exists()) next.add(relationship.id);
              else next.delete(relationship.id);
              return next;
            });
          },
          (error) => {
            console.error(
              "Failed to load ecological relationship report:",
              error,
            );
          },
        ),
      );

      unsubscribers.push(
        onSnapshot(
          doc(
            db,
            "speciesEcologicalRelationships",
            relationship.id,
            "retractionRequests",
            auth.currentUser.uid,
          ),
          (snapshot) => {
            setRetractionStatusByRelationshipId((previous) => {
              const next = new Map(previous);
              if (snapshot.exists()) {
                next.set(
                  relationship.id,
                  String(snapshot.data()?.status || "pending"),
                );
              } else {
                next.delete(relationship.id);
              }
              return next;
            });
          },
          (error) => {
            console.error(
              "Failed to load relationship retraction status:",
              error,
            );
          },
        ),
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [relationships]);

  useEffect(() => {
    if (!enabled || !ownerUid || !auth.currentUser) {
      setCollectionSpecies(new Map());
      return undefined;
    }

    let cancelled = false;

    getDocs(collection(db, "users", ownerUid, "species"))
      .then((snapshot) => {
        if (cancelled) return;

        const nextSpecies = new Map();
        snapshot.docs.forEach((speciesDocument) => {
          const data = speciesDocument.data() || {};
          const key = normalizeSpeciesName(data.name || speciesDocument.id);
          if (!key) return;
          nextSpecies.set(key, { id: speciesDocument.id, ...data });
        });
        setCollectionSpecies(nextSpecies);
      })
      .catch((error) => {
        console.error("Failed to resolve linked species in collection:", error);
        if (!cancelled) setCollectionSpecies(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, ownerUid]);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    function closeOnEscape(event) {
      if (event.key === "Escape" && !saving && !requestingRetractionId) {
        if (retractionTarget) {
          setRetractionTarget(null);
          setRetractionReason("");
        } else {
          setModalOpen(false);
        }
      }
    }

    window.addEventListener("keydown", closeOnEscape);

    window.requestAnimationFrame(() => modalRef.current?.focus());

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [modalOpen, requestingRetractionId, retractionTarget, saving]);

  useEffect(() => {
    setModalOpen(false);
    setMode("browse");
    setSelectedChoice(null);
    setSelectedRelatedSpecies([]);
    setSpeciesQuery("");
    setRetractionTarget(null);
    setRetractionReason("");
    setActionError("");
    setActionMessage("");
    setLinkedSpeciesTransition(null);
  }, [currentSpeciesKey]);

  const relationshipViews = useMemo(
    () =>
      relationships
        .map((relationship) =>
          getRelationshipView(relationship, currentSpeciesKey),
        )
        .filter(Boolean)
        .sort((first, second) =>
          `${first.label}-${first.relatedScientificName}`.localeCompare(
            `${second.label}-${second.relatedScientificName}`,
          ),
        ),
    [currentSpeciesKey, relationships],
  );

  const groupedRelationships = useMemo(() => {
    const groups = new Map();

    relationshipViews.forEach((relationship) => {
      if (!groups.has(relationship.label)) {
        groups.set(relationship.label, {
          label: relationship.label,
          icon: relationship.icon,
          relationships: [],
        });
      }
      groups.get(relationship.label).relationships.push(relationship);
    });

    return [...groups.values()];
  }, [relationshipViews]);

  const searchResults = useMemo(() => {
    const search = normalizeSpeciesName(speciesQuery);
    if (search.length < 2) return [];

    const seen = new Set();

    return speciesNames
      .filter((species) => {
        const scientific = String(species.scientific || "").trim();
        const common = String(species.common || "").trim();
        const key = normalizeSpeciesName(scientific);

        if (!scientific || !key || key === currentSpeciesKey || seen.has(key)) {
          return false;
        }

        const matches =
          normalizeSpeciesName(scientific).includes(search) ||
          normalizeSpeciesName(common).includes(search);

        if (matches) seen.add(key);
        return matches;
      })
      .slice(0, 60);
  }, [currentSpeciesKey, speciesQuery]);

  const currentSpecies = useMemo(
    () => ({
      scientificName: String(scientificName || "").trim(),
      scientificNameKey: currentSpeciesKey,
      commonName: String(commonName || "").trim(),
      kingdom: resolvedKingdom,
      entityType: "species",
      agentType: "",
    }),
    [commonName, currentSpeciesKey, resolvedKingdom, scientificName],
  );

  const selectedRelationshipItems = useMemo(() => {
    if (!selectedChoice || selectedRelatedSpecies.length === 0) return [];

    return selectedRelatedSpecies.map((selectedSpecies) => {
      const isAbiotic = selectedSpecies.entityType === "abiotic";
      const taxonomy = isAbiotic
        ? null
        : resolveTaxonomyPath(selectedSpecies.scientific);
      const relatedSpecies = isAbiotic
        ? {
            scientificName: selectedSpecies.scientific,
            scientificNameKey: `abiotic:${selectedSpecies.agentType}`,
            commonName: selectedSpecies.common || "Abiotic agent",
            kingdom: "abiotic",
            entityType: "abiotic",
            agentType: selectedSpecies.agentType,
          }
        : {
            scientificName: selectedSpecies.scientific,
            scientificNameKey: normalizeSpeciesName(selectedSpecies.scientific),
            commonName: selectedSpecies.common || "",
            kingdom: taxonomy?.kingdom || "",
            entityType: "species",
            agentType: "",
          };

      const endpoints = relationshipEndpoints(
        selectedChoice,
        currentSpecies,
        relatedSpecies,
      );
      const existingRelationship = relationships.find(
        (relationship) =>
          relationship.relationshipType === selectedChoice.predicate &&
          relationship.sourceSpeciesKey ===
            endpoints.source.scientificNameKey &&
          relationship.targetSpeciesKey === endpoints.target.scientificNameKey,
      );

      return {
        selectedSpecies,
        endpoints,
        existingRelationship,
        alreadyReported: Boolean(
          existingRelationship &&
          reportedRelationshipIds.has(existingRelationship.id),
        ),
      };
    });
  }, [
    currentSpecies,
    relationships,
    reportedRelationshipIds,
    selectedChoice,
    selectedRelatedSpecies,
  ]);

  const reportableSelectedRelationshipItems = useMemo(
    () => selectedRelationshipItems.filter((item) => !item.alreadyReported),
    [selectedRelationshipItems],
  );

  function selectedSpeciesKey(species) {
    return species?.entityType === "abiotic"
      ? `abiotic:${species.agentType}`
      : normalizeSpeciesName(species?.scientific);
  }

  function toggleSelectedRelatedSpecies(species) {
    const key = selectedSpeciesKey(species);
    if (!key) return;

    setSelectedRelatedSpecies((previous) => {
      const exists = previous.some((item) => selectedSpeciesKey(item) === key);
      return exists
        ? previous.filter((item) => selectedSpeciesKey(item) !== key)
        : [...previous, species];
    });
    setActionError("");
  }

  function resetAddForm() {
    setSelectedChoice(null);
    setSelectedRelatedSpecies([]);
    setSpeciesQuery("");
    setActionError("");
    setActionMessage("");
  }

  function openRelationships(nextMode = "browse") {
    setMode(nextMode);
    resetAddForm();
    setModalOpen(true);
  }

  function closeRelationships() {
    if (saving || requestingRetractionId) return;
    setModalOpen(false);
    setMode("browse");
    setRetractionTarget(null);
    setRetractionReason("");
    resetAddForm();
  }

  function openSpeciesInCollection(relationship) {
    if (
      relationship.relatedEntityType !== "species" ||
      linkedSpeciesTransition
    ) {
      return;
    }

    const species = collectionSpecies.get(relationship.relatedSpeciesKey);
    const taxonomy = resolveTaxonomyPath(relationship.relatedScientificName);

    if (!species || !taxonomy || !ownerUid) return;

    const basePath =
      ownerUid === auth.currentUser?.uid ? "" : `/explore/${ownerUid}`;
    const firstForm = Array.isArray(species.forms)
      ? species.forms.find((form) => form?.isDefault) || species.forms[0]
      : null;
    const formIndex = Array.isArray(species.forms)
      ? species.forms.indexOf(firstForm)
      : 0;
    const openFormKey = firstForm
      ? firstForm.id || firstForm.url || `form-${Math.max(formIndex, 0)}`
      : undefined;
    const adminModeActive =
      new URLSearchParams(location.search).get("adminMode") === "true" &&
      ownerUid !== auth.currentUser?.uid;
    const search = new URLSearchParams();

    if (adminModeActive) search.set("adminMode", "true");
    const searchSuffix = search.toString() ? `?${search.toString()}` : "";

    setLinkedSpeciesTransition({
      scientificName: relationship.relatedScientificName,
      commonName: relationship.relatedCommonName || "",
      icon: relationship.icon || "mutualism",
    });

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        navigate(
          `${basePath}/kingdom/${routeSegment(
            taxonomy.kingdom,
          )}/phylum/${routeSegment(taxonomy.phylum)}/class/${routeSegment(
            taxonomy.className,
          )}/order/${routeSegment(taxonomy.order)}/family/${routeSegment(
            taxonomy.family,
          )}/genus/${encodeURIComponent(taxonomy.genus)}${searchSuffix}`,
          {
            state: {
              openSpeciesId: species.id,
              ...(openFormKey ? { openFormKey } : {}),
              ecologicalRelationshipTransition: true,
            },
          },
        );
      }, 140);
    });
  }

  async function saveRelationship() {
    if (
      !selectedChoice ||
      reportableSelectedRelationshipItems.length === 0 ||
      saving
    ) {
      return;
    }

    setSaving(true);
    setActionError("");
    setActionMessage("");

    try {
      const reportRelationship = httpsCallable(
        functions,
        "reportEcologicalRelationship",
      );
      const addedRelationshipIds = [];

      for (const item of reportableSelectedRelationshipItems) {
        const result = await reportRelationship({
          relationshipType: selectedChoice.predicate,
          sourceSpecies: item.endpoints.source,
          targetSpecies: item.endpoints.target,
        });
        const relationshipId = String(result.data?.relationshipId || "");
        if (relationshipId) addedRelationshipIds.push(relationshipId);
      }

      if (addedRelationshipIds.length > 0) {
        setReportedRelationshipIds((previous) => {
          const next = new Set(previous);
          addedRelationshipIds.forEach((relationshipId) =>
            next.add(relationshipId),
          );
          return next;
        });
      }

      const count = reportableSelectedRelationshipItems.length;
      setActionMessage(
        `${count} ecological ${count === 1 ? "relationship" : "relationships"} reported.`,
      );
      setMode("browse");
      setSelectedChoice(null);
      setSelectedRelatedSpecies([]);
      setSpeciesQuery("");
    } catch (error) {
      console.error("Failed to report ecological relationships:", error);
      setActionError(
        error?.message || "The ecological relationships could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function requestRetraction() {
    const relationshipId = retractionTarget?.id;
    const reason = retractionReason.trim();

    if (!relationshipId || requestingRetractionId) return;

    if (reason.length < 10) {
      setActionError(
        "Please explain what should be reviewed using at least 10 characters.",
      );
      return;
    }

    setRequestingRetractionId(relationshipId);
    setActionError("");
    setActionMessage("");

    try {
      const requestReview = httpsCallable(
        functions,
        "removeEcologicalRelationshipReport",
      );
      const result = await requestReview({ relationshipId, reason });

      setRetractionStatusByRelationshipId((previous) => {
        const next = new Map(previous);
        next.set(relationshipId, "pending");
        return next;
      });
      setRetractionTarget(null);
      setRetractionReason("");
      setActionMessage(
        result.data?.alreadyPending
          ? "This relationship is already awaiting administrator review."
          : "Flag submitted. An administrator will review it before any change is made.",
      );
    } catch (error) {
      console.error(
        "Failed to request ecological relationship retraction:",
        error,
      );
      setActionError(
        error?.message || "The retraction request could not be submitted.",
      );
    } finally {
      setRequestingRetractionId("");
    }
  }

  if (!enabled || !currentSpeciesKey) return null;

  return (
    <>
      <div className="mt-4 min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#93CA87]/55 bg-[#FCFFF6] shadow-sm">
        <button
          type="button"
          onClick={() => openRelationships("browse")}
          className="flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left transition hover:bg-[#24C3BE]/5"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#374049] text-[#24C3BE]">
            <RelationshipIcon className="h-5 w-5" />
          </span>

          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#374049]">
              Ecological relationships
            </span>
            <span className="block truncate text-xs text-[#374049]/55">
              {relationshipViews.length > 0
                ? `${relationshipViews.length} community-reported ${
                    relationshipViews.length === 1
                      ? "relationship"
                      : "relationships"
                  }`
                : "Community-built species interactions"}
            </span>
          </span>

          <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.12em] text-[#01767A]">
            {relationshipViews.length > 0 ? "View" : "Add"} ›
          </span>
        </button>

        {relationshipError && (
          <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
            {relationshipError}
          </div>
        )}

        {groupedRelationships.length > 0 && (
          <div className="min-w-0 border-t border-[#93CA87]/35 px-4 py-3">
            <div className="flex min-w-0 flex-wrap gap-2">
              {groupedRelationships.map((group) => {
                const firstRelationship = group.relationships[0];
                const species = collectionSpecies.get(
                  firstRelationship.relatedSpeciesKey,
                );
                const taxonomy = resolveTaxonomyPath(
                  firstRelationship.relatedScientificName,
                );
                const canNavigate = Boolean(
                  firstRelationship.relatedEntityType === "species" &&
                  species &&
                  taxonomy,
                );
                const groupSummary = getGroupedRelationshipSummary(group);

                return (
                  <button
                    key={group.label}
                    type="button"
                    onClick={() => {
                      if (group.relationships.length === 1 && canNavigate) {
                        openSpeciesInCollection(firstRelationship);
                      } else {
                        openRelationships("browse");
                      }
                    }}
                    className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-xl border border-[#93CA87]/45 bg-white px-2.5 py-2 text-left text-xs text-[#374049] shadow-sm transition hover:border-[#24C3BE]/70 hover:bg-[#24C3BE]/5"
                    title={
                      group.relationships.length === 1 &&
                      firstRelationship.relatedEntityType === "species" &&
                      !canNavigate
                        ? "This species is not available in the collection currently being explored."
                        : undefined
                    }
                  >
                    <RelationshipIcon
                      kind={group.icon}
                      className="h-4 w-4 shrink-0 text-[#01767A]"
                    />
                    <span className="min-w-0">
                      {groupSummary ? (
                        <span className="font-semibold text-[#01767A]">
                          {groupSummary}
                        </span>
                      ) : (
                        <>
                          <span className="font-semibold text-[#01767A]">
                            {group.label}
                          </span>{" "}
                          <span
                            className={
                              firstRelationship.relatedEntityType === "species"
                                ? "italic"
                                : "font-medium"
                            }
                          >
                            {firstRelationship.relatedScientificName}
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-5"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeRelationships();
          }}
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ecological-relationships-title"
            className="flex max-h-[100svh] w-full flex-col overflow-hidden rounded-t-[2rem] bg-[#f4f6f8] shadow-2xl outline-none sm:max-h-[88vh] sm:max-w-3xl sm:rounded-[2rem]"
          >
            <div className="shrink-0 border-b border-[#93CA87]/35 bg-white px-5 py-4 sm:px-6">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#374049] text-[#24C3BE]">
                  <RelationshipIcon className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#01767A]">
                    Shared species-level information
                  </div>
                  <h2
                    id="ecological-relationships-title"
                    className="truncate text-2xl italic leading-tight text-[#374049] sm:text-3xl"
                    style={{ fontFamily: "Cormorant Garamond" }}
                  >
                    {scientificName}
                  </h2>
                  <p className="text-xs text-[#374049]/55">
                    Ecological relationships reported by the BioIndex community
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeRelationships}
                  disabled={saving || Boolean(requestingRetractionId)}
                  aria-label="Close ecological relationships"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#93CA87]/40 bg-white text-xl text-[#374049] transition hover:bg-[#FCFFF6] disabled:opacity-50"
                >
                  ×
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 rounded-xl border border-[#93CA87]/35 bg-[#FCFFF6] p-1">
                <button
                  type="button"
                  onClick={() => {
                    setMode("browse");
                    resetAddForm();
                  }}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                    mode === "browse"
                      ? "bg-[#374049] text-white shadow-sm"
                      : "text-[#374049] hover:bg-white"
                  }`}
                >
                  Reported relationships
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("add");
                    resetAddForm();
                  }}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                    mode === "add"
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "text-[#374049] hover:bg-white"
                  }`}
                >
                  Add relationship
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
              {actionMessage && (
                <div className="mb-4 rounded-xl border border-[#93CA87]/50 bg-[#FCFFF6] px-4 py-3 text-sm text-[#01767A]">
                  {actionMessage}
                </div>
              )}

              {actionError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {actionError}
                </div>
              )}

              {mode === "browse" ? (
                groupedRelationships.length > 0 ? (
                  <div className="space-y-4">
                    {groupedRelationships.map((group) => (
                      <section
                        key={group.label}
                        className="overflow-hidden rounded-2xl border border-[#93CA87]/45 bg-white shadow-sm"
                      >
                        <div className="flex items-center gap-2 border-b border-[#93CA87]/30 bg-[#FCFFF6] px-4 py-3">
                          <RelationshipIcon
                            kind={group.icon}
                            className="h-5 w-5 shrink-0 text-[#01767A]"
                          />
                          <h3 className="text-sm font-semibold text-[#374049]">
                            {group.label}
                          </h3>
                          <span className="ml-auto rounded-full bg-[#374049] px-2 py-0.5 text-[10px] font-semibold text-white">
                            {group.relationships.length}
                          </span>
                        </div>

                        <div className="divide-y divide-[#93CA87]/20">
                          {group.relationships.map((relationship) => {
                            const species = collectionSpecies.get(
                              relationship.relatedSpeciesKey,
                            );
                            const taxonomy = resolveTaxonomyPath(
                              relationship.relatedScientificName,
                            );
                            const canNavigate = Boolean(
                              relationship.relatedEntityType === "species" &&
                              species &&
                              taxonomy,
                            );
                            const reportedByCurrentUser =
                              reportedRelationshipIds.has(relationship.id);
                            const retractionStatus =
                              retractionStatusByRelationshipId.get(
                                relationship.id,
                              ) || "";
                            const retractionPending =
                              retractionStatus === "pending";

                            return (
                              <div
                                key={relationship.id}
                                className="flex min-w-0 items-center gap-3 px-4 py-3"
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    canNavigate &&
                                    openSpeciesInCollection(relationship)
                                  }
                                  disabled={!canNavigate}
                                  className={`min-w-0 flex-1 text-left ${
                                    canNavigate
                                      ? "group cursor-pointer"
                                      : "cursor-default"
                                  }`}
                                  title={
                                    canNavigate
                                      ? `Open ${relationship.relatedScientificName} in this collection`
                                      : relationship.relatedEntityType ===
                                          "abiotic"
                                        ? "Abiotic ecological agent"
                                        : "This species is not available in the collection currently being explored."
                                  }
                                >
                                  <span
                                    className={`block truncate text-base ${
                                      relationship.relatedEntityType ===
                                      "species"
                                        ? "italic"
                                        : "font-semibold"
                                    } ${
                                      canNavigate
                                        ? "text-[#01767A] group-hover:underline"
                                        : "text-[#374049]"
                                    }`}
                                    style={{
                                      fontFamily: "Cormorant Garamond",
                                    }}
                                  >
                                    {relationship.relatedScientificName}
                                  </span>
                                  <span className="block truncate text-xs text-[#374049]/55">
                                    {relationship.relatedCommonName ||
                                      "No common name recorded"}
                                    {relationship.relatedEntityType ===
                                    "abiotic"
                                      ? ""
                                      : canNavigate
                                        ? " · Open in this collection"
                                        : " · Not in this collection"}
                                  </span>
                                </button>

                                <div className="shrink-0 text-right">
                                  <div className="text-[10px] text-[#374049]/50">
                                    {relationship.reportCount || 1}{" "}
                                    {(relationship.reportCount || 1) === 1
                                      ? "report"
                                      : "reports"}
                                    {reportedByCurrentUser
                                      ? " · Reported by you"
                                      : ""}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setRetractionTarget(relationship);
                                      setRetractionReason("");
                                      setActionError("");
                                    }}
                                    disabled={
                                      retractionPending ||
                                      requestingRetractionId === relationship.id
                                    }
                                    className={`mt-1 text-[10px] font-medium transition hover:underline disabled:no-underline ${
                                      retractionPending
                                        ? "text-amber-600/80"
                                        : "text-red-400 hover:text-red-500"
                                    } disabled:opacity-70`}
                                  >
                                    {requestingRetractionId === relationship.id
                                      ? "Submitting…"
                                      : retractionPending
                                        ? "Review pending"
                                        : "Flag for review"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-[#93CA87]/60 bg-white px-6 py-10 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#374049] text-[#24C3BE]">
                      <RelationshipIcon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-[#374049]">
                      No relationships reported yet
                    </h3>
                    <p className="mx-auto mt-1 max-w-md text-sm leading-relaxed text-[#374049]/60">
                      Add a documented interaction by linking this species to
                      another species already recognized by BioIndex.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setMode("add");
                        resetAddForm();
                      }}
                      className="mt-5 rounded-xl bg-[#24C3BE] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#01767A]"
                    >
                      Add the first relationship
                    </button>
                  </div>
                )
              ) : (
                <div className="space-y-5">
                  <section>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-[#374049]">
                        1. Choose the relationship
                      </h3>
                      {selectedChoice && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedChoice(null);
                            setSelectedRelatedSpecies([]);
                            setSpeciesQuery("");
                          }}
                          className="text-xs font-semibold text-[#01767A] hover:underline"
                        >
                          Change
                        </button>
                      )}
                    </div>

                    {selectedChoice ? (
                      <div className="flex items-center gap-3 rounded-2xl border border-[#24C3BE]/50 bg-white px-4 py-3 shadow-sm">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#374049] text-[#24C3BE]">
                          <RelationshipIcon
                            kind={
                              RELATIONSHIP_DEFINITIONS[selectedChoice.predicate]
                                ?.icon
                            }
                            className="h-5 w-5"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-[#374049]">
                            {selectedChoice.label}
                          </span>
                          <span className="block text-xs text-[#374049]/55">
                            {selectedChoice.description}
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {choices.map((choice) => (
                          <button
                            key={choice.id}
                            type="button"
                            onClick={() => {
                              setSelectedChoice(choice);
                              setSelectedRelatedSpecies([]);
                              setSpeciesQuery("");
                              setActionError("");
                            }}
                            className="flex items-start gap-3 rounded-2xl border border-[#93CA87]/45 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#24C3BE]/70 hover:shadow-md"
                          >
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#374049] text-[#24C3BE]">
                              <RelationshipIcon
                                kind={
                                  RELATIONSHIP_DEFINITIONS[choice.predicate]
                                    ?.icon
                                }
                                className="h-4 w-4"
                              />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-[#374049]">
                                {choice.label}
                              </span>
                              <span className="mt-0.5 block text-xs leading-relaxed text-[#374049]/55">
                                {choice.description}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </section>

                  {selectedChoice && (
                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-[#374049]">
                        2. Select one or more linked species or agents
                      </h3>

                      {selectedRelatedSpecies.length > 0 && (
                        <div className="mb-3 rounded-2xl border border-[#24C3BE]/50 bg-[#FCFFF6] p-3 shadow-sm">
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#01767A]">
                              Selected ({selectedRelatedSpecies.length})
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedRelatedSpecies([])}
                              className="text-[11px] font-semibold text-[#01767A] hover:underline"
                            >
                              Clear all
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedRelatedSpecies.map((species) => (
                              <button
                                key={selectedSpeciesKey(species)}
                                type="button"
                                onClick={() =>
                                  toggleSelectedRelatedSpecies(species)
                                }
                                className="inline-flex max-w-full items-center gap-2 rounded-xl border border-[#93CA87]/50 bg-white px-3 py-2 text-left text-xs text-[#374049] shadow-sm transition hover:border-red-300 hover:bg-red-50/50"
                                title="Remove selection"
                              >
                                <span
                                  className={
                                    species.entityType === "abiotic"
                                      ? "font-semibold"
                                      : "italic"
                                  }
                                >
                                  {species.scientific}
                                </span>
                                <span className="text-sm leading-none text-[#374049]/45">
                                  ×
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="overflow-hidden rounded-2xl border border-[#93CA87]/45 bg-white shadow-sm">
                        {availableAbioticAgents.length > 0 && (
                          <div className="border-b border-[#93CA87]/30 bg-[#FCFFF6] p-3">
                            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#01767A]">
                              Abiotic agents
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {availableAbioticAgents.map((agent) => {
                                const candidate = {
                                  ...agent,
                                  entityType: "abiotic",
                                };
                                const selected = selectedRelatedSpecies.some(
                                  (item) =>
                                    selectedSpeciesKey(item) ===
                                    selectedSpeciesKey(candidate),
                                );

                                return (
                                  <button
                                    key={agent.agentType}
                                    type="button"
                                    onClick={() =>
                                      toggleSelectedRelatedSpecies(candidate)
                                    }
                                    title={agent.description}
                                    className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold shadow-sm transition ${
                                      selected
                                        ? "border-[#24C3BE] bg-[#24C3BE]/10 text-[#01767A]"
                                        : "border-[#93CA87]/50 bg-white text-[#374049] hover:border-[#24C3BE] hover:bg-[#24C3BE]/5"
                                    }`}
                                  >
                                    <RelationshipIcon
                                      kind={
                                        selectedChoice.predicate ===
                                        "pollinates"
                                          ? "pollination"
                                          : "seed"
                                      }
                                      className="h-4 w-4 shrink-0 text-[#01767A]"
                                    />
                                    {agent.scientific}
                                    {selected && (
                                      <span aria-hidden="true">✓</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="mt-2 text-[11px] leading-relaxed text-[#374049]/55">
                              Select any applicable agents above, or search for
                              biological species below.
                            </p>
                          </div>
                        )}
                        <div className="border-b border-[#93CA87]/30 p-3">
                          <label
                            htmlFor="ecological-species-search"
                            className="sr-only"
                          >
                            Search scientific or common names
                          </label>
                          <div className="flex items-center gap-2 rounded-xl border border-[#93CA87]/50 bg-[#f4f6f8] px-3 py-2.5 focus-within:border-[#24C3BE] focus-within:ring-2 focus-within:ring-[#24C3BE]/15">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 text-[#01767A]"
                            >
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-4-4" />
                            </svg>
                            <input
                              id="ecological-species-search"
                              value={speciesQuery}
                              onChange={(event) =>
                                setSpeciesQuery(event.target.value)
                              }
                              autoComplete="off"
                              placeholder="Search scientific or common name…"
                              className="min-w-0 flex-1 bg-transparent text-sm text-[#374049] outline-none placeholder:text-[#374049]/40"
                            />
                          </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto overscroll-contain">
                          {speciesQuery.trim().length < 2 ? (
                            <div className="px-4 py-8 text-center text-sm text-[#374049]/50">
                              Enter at least two characters to search the
                              BioIndex name collection.
                            </div>
                          ) : searchResults.length > 0 ? (
                            searchResults.map((species) => {
                              const selected = selectedRelatedSpecies.some(
                                (item) =>
                                  selectedSpeciesKey(item) ===
                                  selectedSpeciesKey(species),
                              );

                              return (
                                <button
                                  key={species.scientific}
                                  type="button"
                                  onClick={() =>
                                    toggleSelectedRelatedSpecies(species)
                                  }
                                  className={`flex w-full min-w-0 items-center gap-3 border-b border-[#93CA87]/20 px-4 py-3 text-left transition last:border-b-0 ${
                                    selected
                                      ? "bg-[#24C3BE]/10"
                                      : "hover:bg-[#24C3BE]/5"
                                  }`}
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#374049] text-[#24C3BE]">
                                    <RelationshipIcon className="h-4 w-4" />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span
                                      className="block truncate text-base italic text-[#374049]"
                                      style={{
                                        fontFamily: "Cormorant Garamond",
                                      }}
                                    >
                                      {species.scientific}
                                    </span>
                                    <span className="block truncate text-xs text-[#374049]/55">
                                      {species.common ||
                                        "No common name recorded"}
                                    </span>
                                  </span>
                                  <span
                                    className={`shrink-0 text-xs font-semibold ${
                                      selected
                                        ? "text-[#01767A]"
                                        : "text-[#374049]/45"
                                    }`}
                                  >
                                    {selected ? "Selected ✓" : "Add"}
                                  </span>
                                </button>
                              );
                            })
                          ) : (
                            <div className="px-4 py-8 text-center text-sm text-[#374049]/50">
                              No matching BioIndex species name.
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {selectedChoice && selectedRelationshipItems.length > 0 && (
                    <section className="rounded-2xl border border-[#93CA87]/45 bg-[#FCFFF6] p-4">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#01767A]">
                        Relationship preview
                      </div>
                      <div className="mt-2 space-y-2">
                        {selectedRelationshipItems.map((item) => (
                          <div
                            key={selectedSpeciesKey(item.selectedSpecies)}
                            className="text-sm text-[#374049]"
                          >
                            <span className="italic">{scientificName}</span>{" "}
                            <span className="font-semibold lowercase">
                              {selectedChoice.label}
                            </span>{" "}
                            <span
                              className={
                                item.selectedSpecies.entityType === "abiotic"
                                  ? "font-medium"
                                  : "italic"
                              }
                            >
                              {item.selectedSpecies.scientific}
                            </span>
                            .
                            {item.alreadyReported && (
                              <span className="ml-2 text-xs font-semibold text-[#01767A]">
                                Already reported by you
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>

            {mode === "add" && (
              <div className="shrink-0 border-t border-[#93CA87]/35 bg-white px-4 py-3 sm:px-6">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMode("browse");
                      resetAddForm();
                    }}
                    disabled={saving}
                    className="rounded-xl border border-[#93CA87]/45 bg-white px-4 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-[#FCFFF6] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveRelationship}
                    disabled={
                      saving ||
                      !selectedChoice ||
                      reportableSelectedRelationshipItems.length === 0
                    }
                    className="rounded-xl bg-[#24C3BE] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#01767A] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving
                      ? "Saving…"
                      : reportableSelectedRelationshipItems.length === 1
                        ? "Report 1 relationship"
                        : reportableSelectedRelationshipItems.length > 1
                          ? `Report ${reportableSelectedRelationshipItems.length} relationships`
                          : "Report relationships"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {linkedSpeciesTransition && (
        <div
          className="fixed inset-0 z-[220] flex items-center justify-center bg-[#374049]/95 px-6 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label={`Opening ${linkedSpeciesTransition.scientificName}`}
        >
          <div className="flex max-w-sm flex-col items-center text-center text-white">
            <div className="relative flex h-16 w-16 items-center justify-center rounded-3xl border border-[#93CA87]/35 bg-white/10 text-[#24C3BE] shadow-2xl">
              <span className="absolute inset-0 animate-ping rounded-3xl border border-[#24C3BE]/35" />
              <RelationshipIcon
                kind={linkedSpeciesTransition.icon}
                className="h-8 w-8"
              />
            </div>

            <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#93CA87]">
              Opening linked species
            </div>
            <div
              className="mt-1 text-3xl italic leading-tight"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {linkedSpeciesTransition.scientificName}
            </div>
            {linkedSpeciesTransition.commonName && (
              <div className="mt-1 text-sm text-white/65">
                {linkedSpeciesTransition.commonName}
              </div>
            )}

            <div className="mt-5 h-1 w-32 overflow-hidden rounded-full bg-white/15">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-[#24C3BE]" />
            </div>
          </div>
        </div>
      )}

      {retractionTarget && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 px-4 py-6">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="retraction-request-title"
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-white shadow-2xl"
          >
            <div className="border-b border-[#93CA87]/30 bg-[#FCFFF6] px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700">
                Administrator review required
              </div>
              <h3
                id="retraction-request-title"
                className="mt-1 text-2xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Flag relationship for review
              </h3>
            </div>

            <div className="space-y-4 px-5 py-5">
              <p className="text-sm leading-relaxed text-[#374049]/75">
                This request will be sent to a BioIndex administrator. The
                ecological relationship will remain visible and unchanged until
                the administrator reviews the report and decides whether it
                should be retracted.
              </p>

              <div className="rounded-2xl border border-[#93CA87]/40 bg-[#f4f6f8] px-4 py-3 text-sm text-[#374049]">
                <span className="font-semibold text-[#01767A]">
                  {retractionTarget.label}
                </span>{" "}
                <span
                  className={
                    retractionTarget.relatedEntityType === "species"
                      ? "italic"
                      : "font-medium"
                  }
                >
                  {retractionTarget.relatedScientificName}
                </span>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="ecological-retraction-reason"
                    className="text-xs font-semibold text-[#374049]"
                  >
                    What should the administrator review?
                  </label>
                  <span className="text-[10px] text-[#374049]/45">
                    {retractionReason.length}/1000
                  </span>
                </div>
                <textarea
                  id="ecological-retraction-reason"
                  value={retractionReason}
                  onChange={(event) =>
                    setRetractionReason(event.target.value.slice(0, 1000))
                  }
                  rows={4}
                  maxLength={1000}
                  autoFocus
                  placeholder="Explain what may be incorrect or misleading about this relationship…"
                  className="w-full resize-y rounded-2xl border border-[#93CA87]/50 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15"
                />
              </div>

              {actionError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {actionError}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-[#93CA87]/30 bg-[#f4f6f8] px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setRetractionTarget(null);
                  setRetractionReason("");
                  setActionError("");
                }}
                disabled={Boolean(requestingRetractionId)}
                className="rounded-xl border border-[#374049]/20 bg-white px-4 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-[#FCFFF6] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={requestRetraction}
                disabled={
                  Boolean(requestingRetractionId) ||
                  retractionReason.trim().length < 10
                }
                className="rounded-xl bg-[#374049] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01767A] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {requestingRetractionId ? "Submitting…" : "Send for review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
